import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { AppHeader } from '@components/AppHeader/AppHeader';
import { ErrorMessage, Form, FormCheckbox, FormInput, FormRate, FormTextarea, SubmitButton } from '@components/Forms';
import Loader from '@components/Loader/Loader';
import NoResults from '@components/NoResults/NoResults';
import { ERoutes } from '@enums/routes.enum';
import useApi from '@hooks/useApi';
import type { RootState } from '@store/store';
import type { Rate } from '@store/trips/tripsSlice';

import classes from './RateTrip.module.scss';

const RateTrip = () => {
	const vehicleId = useSelector((state: RootState) => state.auth.vehicleId);
	const rateTripId = useSelector((state: RootState) => state.trips.currentTrip?.rate?.id);
	const rateTripName = useSelector((state: RootState) => state.trips.currentTrip?.rate?.name);
	const rateTripPhone = useSelector((state: RootState) => state.trips.currentTrip?.rate?.phone);
	const rateTripContactMe = useSelector((state: RootState) => state.trips.currentTrip?.rate?.contactMe);
	const rateTripFlightNumber = useSelector((state: RootState) => state.trips.currentTrip?.rate?.flightNumber);
	const rateTripRating = useSelector((state: RootState) => state.trips.currentTrip?.rate?.rating);
	const rateTripFeedback = useSelector((state: RootState) => state.trips.currentTrip?.rate?.feedback);
	const id = useSelector((state: RootState) => state.trips.currentTrip?.id);
	const driverId = useSelector((state: RootState) => state.trips.currentTrip?.driver?.id);
	const showGoBack = useSelector((state: RootState) => state.trips?.showGoBack);
	const error = useSelector((state: RootState) => state.auth.error);
	const isLoading = useSelector((state: RootState) => state.auth.isLoading);

	const isQRMode = !!vehicleId;
	const disabledMode = !!rateTripId;

	const { t } = useTranslation();
	const navigate = useNavigate();
	const { createFeedback, getTripsByVehicleId } = useApi();

	const handleGoBack = () => {
		navigate(`${ERoutes.tripInfo}/${id}`);
	};

	const handleSubmit = async (values: Omit<Rate, 'id'>) => {
		createFeedback(values, id as number, driverId, isQRMode);
	};

	const validationSchema = Yup.object().shape({
		name: Yup.string().when('contactMe', {
			is: true,
			then: (schema) => schema.required(`${t('rateTrip.form.name.label')} ${t('rateTrip.form.validation.required')}`),
			otherwise: (schema) => schema.notRequired(),
		}),
		phone: Yup.string().when('contactMe', {
			is: true,
			then: (schema) => schema.required(`${t('rateTrip.form.phone.label')} ${t('rateTrip.form.validation.required')}`),
			otherwise: (schema) => schema.notRequired(),
		}),
		rating: Yup.number()
			.min(1, `${t('rateTrip.form.rating.label')} ${t('rateTrip.form.validation.min', { min: 1 })}`)
			.max(5, `${t('rateTrip.form.rating.label')} ${t('rateTrip.form.validation.max', { max: 5 })}`),
		feedback: Yup.string()
			.max(2500, `${t('rateTrip.form.feedback.label')} ${t('rateTrip.form.validation.maxLength', { max: 2500 })}`)
			.when('rating', {
				is: (rating?: number) => !rating,
				then: (schema) => schema.required(t('rateTrip.form.validation.ratingOrFeedback')),
				otherwise: (schema) => schema.notRequired(),
			}),
	});

	const initialValues: Omit<Rate, 'id'> = {
		name: rateTripName,
		phone: rateTripPhone,
		contactMe: !!rateTripContactMe,
		flightNumber: rateTripFlightNumber,
		rating: rateTripRating,
		feedback: rateTripFeedback,
	};

	useEffect(() => {
		if (isQRMode && !id) {
			getTripsByVehicleId(vehicleId as number);
		}
	}, [isQRMode]);

	return isLoading ? (
		<Loader />
	) : isQRMode && !id ? (
		<NoResults />
	) : (
		<div className={classes['container']}>
			<AppHeader goBack={showGoBack ? handleGoBack : undefined} title={isQRMode ? t('rateTrip.title') : undefined} />
			<div className={classes['formContainer']}>
				<Form<Omit<Rate, 'id'>> initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
					<FormInput
						disabled={disabledMode}
						displayError={false}
						name="name"
						placeholder={t('rateTrip.form.name.placeholder')}
						firstValue={initialValues.name}
					/>
					<FormInput
						disabled={disabledMode}
						displayError={false}
						name="phone"
						numbersOnly
						placeholder={t('rateTrip.form.phone.placeholder')}
						firstValue={initialValues.phone}
					/>
					{initialValues.flightNumber && (
						<FormInput displayError={false} name="flightNumber" firstValue={initialValues.flightNumber} disabled />
					)}
					<FormCheckbox
						displayError={false}
						name="contactMe"
						title={t('rateTrip.form.contactMe.label')}
						firstValue={initialValues.contactMe}
						disabled={disabledMode}
					/>
					<FormRate displayError={false} name="rating" firstValue={initialValues.rating} disabled={disabledMode} />
					<FormTextarea
						name="feedback"
						placeholder={t('rateTrip.form.feedback.placeholder')}
						firstValue={initialValues.feedback}
						id={'feedback'}
						disabled={disabledMode}
					/>
					<ErrorMessage error={error} visible={!!error} />
					<SubmitButton disabled={disabledMode}>{t('rateTrip.form.submitButton')}</SubmitButton>
				</Form>
			</div>
		</div>
	);
};

export default RateTrip;
